import { FC, useEffect, useRef, useState } from 'react';
import {
  type AnnotoriousOpenSeadragonAnnotator,
  type ImageAnnotation,
  useAnnotator,
  useViewer,
} from '@annotorious/react';
import OpenSeadragon from 'openseadragon';

import {
  type AnnotationRect,
  getAnnotationProperties,
  getAnnotationRect,
} from '@/lib/annotations';

/**
 * Pointer travel (viewer px) below which a Shift + press-release counts as a
 * click, not a rubber-band drag.
 */
const DRAG_THRESHOLD_PX = 4;

/** Rubber-band rectangle in viewer-element pixels, ready for CSS positioning. */
interface BandRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Point {
  x: number;
  y: number;
}

const toBandRect = (start: Point, end: Point): BandRect => ({
  left: Math.min(start.x, end.x),
  top: Math.min(start.y, end.y),
  width: Math.abs(start.x - end.x),
  height: Math.abs(start.y - end.y),
});

const rectsIntersect = (first: AnnotationRect, second: AnnotationRect) =>
  first.x < second.x + second.w &&
  second.x < first.x + first.w &&
  first.y < second.y + second.h &&
  second.y < first.y + first.h;

const rectContains = (rect: AnnotationRect, point: Point) =>
  point.x >= rect.x &&
  point.x <= rect.x + rect.w &&
  point.y >= rect.y &&
  point.y <= rect.y + rect.h;

interface RubberBandSelectionProps {
  /** Receives the ids of every committed box intersecting the released band. */
  onSelectAnnotations?: (ids: Array<string>) => void;
  /**
   * Fallback for a Shift + click without drag: the pointerdown was swallowed
   * before Annotorious could see it, so the toggle is replayed here.
   */
  onToggleAnnotation?: (id: string) => void;
}

/**
 * Shift + drag rubber-band selection over the shelf image. Both panning and
 * drawing already own plain click-drag (see `annotator.tsx`), so the band
 * claims the Shift-modified drag instead: a capture-phase `pointerdown`
 * listener on the OpenSeadragon element swallows the event before OSD can pan
 * or Annotorious can draw, then window-level listeners track the drag so it
 * survives leaving the viewer. On release the band is converted from viewer
 * pixels to natural image pixels and intersected with every committed
 * (non-`temp`) box; the hits are handed to the workspace, whose `selectedIds`
 * drive the persistent red highlight.
 *
 * Because the pointerdown never reaches Annotorious, its Shift + click toggle
 * (`use-selection-bridge.ts`) can't fire either — a sub-threshold release
 * replays that toggle against the smallest box under the cursor.
 */
export const RubberBandSelection: FC<RubberBandSelectionProps> = ({
  onSelectAnnotations,
  onToggleAnnotation,
}: RubberBandSelectionProps) => {
  // The declared return type is non-nullable, but the context holds no viewer
  // until OpenSeadragon has initialized.
  const viewer = useViewer() as OpenSeadragon.Viewer | undefined;
  const annotator = useAnnotator<
    AnnotoriousOpenSeadragonAnnotator | undefined
  >();
  const [band, setBand] = useState<BandRect | null>(null);
  // Latest callbacks behind a stable ref, so a re-render mid-drag can't tear
  // down the window listeners tracking the gesture.
  const callbacksRef = useRef({ onSelectAnnotations, onToggleAnnotation });
  callbacksRef.current = { onSelectAnnotations, onToggleAnnotation };

  useEffect(() => {
    if (!viewer || !annotator) return;
    const { element } = viewer;

    let origin: Point | null = null;
    let dragListeners: AbortController | null = null;

    const toElementPoint = (event: PointerEvent): Point => {
      const bounds = element.getBoundingClientRect();
      return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    };

    const toImagePoint = (point: Point): Point =>
      viewer.viewport.viewerElementToImageCoordinates(
        new OpenSeadragon.Point(point.x, point.y)
      );

    const committedAnnotations = (): Array<ImageAnnotation> =>
      annotator
        .getAnnotations()
        .filter((annotation) => !getAnnotationProperties(annotation).temp);

    // The swallowed pointerdown keeps Annotorious blind to the gesture, but
    // browsers may still synthesize a `click` from the pointerup — swallow it
    // too, so the click can't reach Annotorious and toggle a box twice.
    const swallowClick = (event: MouseEvent) => {
      event.stopPropagation();
    };

    const handleMove = (event: PointerEvent) => {
      if (!origin) return;
      event.preventDefault();
      setBand(toBandRect(origin, toElementPoint(event)));
    };

    const endDrag = () => {
      origin = null;
      setBand(null);
      dragListeners?.abort();
      dragListeners = null;
      // The synthesized click fires after pointerup; keep the guard alive
      // until the browser has had its chance to dispatch it.
      window.setTimeout(() => {
        element.removeEventListener('click', swallowClick, true);
      }, 0);
    };

    const handleUp = (event: PointerEvent) => {
      if (!origin) return;
      const current = toElementPoint(event);
      const rect = toBandRect(origin, current);
      const isDrag =
        rect.width >= DRAG_THRESHOLD_PX || rect.height >= DRAG_THRESHOLD_PX;

      if (isDrag) {
        const topLeft = toImagePoint({ x: rect.left, y: rect.top });
        const bottomRight = toImagePoint({
          x: rect.left + rect.width,
          y: rect.top + rect.height,
        });
        const imageRect: AnnotationRect = {
          x: topLeft.x,
          y: topLeft.y,
          w: bottomRight.x - topLeft.x,
          h: bottomRight.y - topLeft.y,
        };

        const ids = committedAnnotations()
          .filter((annotation) =>
            rectsIntersect(imageRect, getAnnotationRect(annotation))
          )
          .map((annotation) => annotation.id);
        if (ids.length > 0) {
          callbacksRef.current.onSelectAnnotations?.(ids);
        }
      } else {
        // Overlapping boxes resolve to the smallest one under the cursor,
        // i.e. the most specific hit.
        const imagePoint = toImagePoint(current);
        const hit = committedAnnotations()
          .map((annotation) => ({
            id: annotation.id,
            rect: getAnnotationRect(annotation),
          }))
          .filter(({ rect: boxRect }) => rectContains(boxRect, imagePoint))
          .sort(
            (left, right) =>
              left.rect.w * left.rect.h - right.rect.w * right.rect.h
          )[0];
        if (hit) {
          callbacksRef.current.onToggleAnnotation?.(hit.id);
        }
      }

      endDrag();
    };

    const handleDown = (event: PointerEvent) => {
      if (!event.shiftKey || event.button !== 0) return;
      // Capture phase: OSD (pan) and Annotorious (draw/click) never see it.
      event.stopPropagation();
      event.preventDefault();

      origin = toElementPoint(event);
      setBand(toBandRect(origin, origin));
      element.addEventListener('click', swallowClick, true);
      dragListeners = new AbortController();
      const options = { capture: true, signal: dragListeners.signal };
      window.addEventListener('pointermove', handleMove, options);
      window.addEventListener('pointerup', handleUp, options);
      window.addEventListener('pointercancel', endDrag, options);
    };

    element.addEventListener('pointerdown', handleDown, true);
    return () => {
      element.removeEventListener('pointerdown', handleDown, true);
      element.removeEventListener('click', swallowClick, true);
      endDrag();
    };
  }, [viewer, annotator]);

  if (!band) return null;

  return (
    <div
      className='pointer-events-none absolute z-10 border-2 border-dashed border-selected bg-selected/10'
      style={{
        left: band.left,
        top: band.top,
        width: band.width,
        height: band.height,
      }}
    />
  );
};
